/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jan 15 2024
 **/

import { useTheme } from "@emotion/react";
import { useEffect, useState } from "react";

import {
	CircularProgress,
	Column,
	Extensions,
	Loader,
	Row,
	Typography,
	useModal
} from "@/frameworks/designStuff/components";
import { Configure, downloadBlobData } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Each, Maybe } from "@/frameworks/libraries/components";
import { useFileExtensions } from "@/frameworks/core/utils";
import { useFileManager, useTusUploadStateRenderer } from "@/frameworks/core/hooks";
import { generateVideoThumbnail } from "@/frameworks/libraries/utilities/videoToThumbnail";

import * as S from "../emotion";
import { DocumentViewer } from "../../../DocumentViewer";
import { ModalID } from "@/frameworks/core/commons";

type ChatFile = {
	file: File;
	displaySize: string;
	isLoading: boolean;
	id: string;
	tusID: string;
	url?: string;
	videoThumbnailPath?: string;
};
type LocalFile = { file: File; displaySize: string };

type UploadedFilesProps = {
	files?: ChatFile[];
	filesBlobs?: string[];
	localFiles?: LocalFile[];
	onRemove?: (tusID: string, fileID?: string) => void;
};

export default function UploadedFiles({ files, localFiles, onRemove }: UploadedFilesProps) {
	const { getFileExtension, getDroppedFileMimeType } = useFileExtensions();

	const images = files?.filter((v) => getFileExtension(v.file.type) === "image");
	const videos = files?.filter((v) => getFileExtension(v.file.type) === "video");
	const otherFiles = files?.filter(
		(v) => getFileExtension(v.file.type) !== "image" && getFileExtension(v.file.type) !== "video"
	);

	const localImages = localFiles
		?.filter((v) => getFileExtension(v.file.type) === "image")
		.map((image) => window.URL.createObjectURL(image.file));
	const localOtherImages = localFiles?.filter((v) => getFileExtension(v.file.type) !== "image");

	return (
		<S.FileWrapper gap={4}>
			<Row.li gap={4} css={{ width: "100%", flexWrap: "wrap" }}>
				<Each
					render={(imageFile) => (
						<ImageFile
							key={imageFile.id}
							only={images?.length === 1}
							file={imageFile.file}
							id={imageFile.id}
							url={imageFile.url}
							tusID={imageFile.tusID}
							isLoading={imageFile.isLoading}
							onRemove={() => onRemove && onRemove(imageFile.tusID, imageFile.id)}
						/>
					)}
					of={images}
				/>
				<Each
					render={(videoFile) => (
						<VideoFile
							key={videoFile.id}
							only={videos?.length === 1}
							file={videoFile.file}
							id={videoFile.id}
							tusID={videoFile.tusID}
							isLoading={videoFile.isLoading}
							onRemove={() => onRemove && onRemove(videoFile.tusID, videoFile.id)}
						/>
					)}
					of={videos}
				/>
				<Each
					render={(imageUrl) => (
						<ImageFile key={imageUrl} only={localImages?.length === 1} localFile={imageUrl} />
					)}
					of={localImages}
				/>
			</Row.li>

			<Row.li gap={4} css={{ width: "100%", flexWrap: "wrap" }}>
				<Each
					render={(file) => (
						<S.FileItem key={file.file.name} gap={12}>
							<Maybe test={file.isLoading}>
								<FileLoader tusID={file.tusID} />
							</Maybe>
							{/* hwp, hwpx에 대한 타입이 없으므로 getDroppedFileMimeType 으로 한번 더 감싸 타입체크진행 */}
							<Extensions
								type={getFileExtension(getDroppedFileMimeType(file.file.type, file.file.name))}
							/>
							<Column css={{ width: "calc(100% - 56px)" }}>
								<Typography
									typoColor="neutral_80_100"
									typoSize="Body3_MD"
									css={{
										textOverflow: "ellipsis",
										overflow: "hidden",
										whiteSpace: "nowrap",
										width: "100%"
									}}
								>
									{file.file.name}
								</Typography>
								<Typography typoColor="neutral_60_100" typoSize="Body4">
									{_T("용량 {{size}}", { size: file.displaySize })}
								</Typography>
							</Column>
							<Maybe test={!file.isLoading}>
								<S.RemoveFile
									id="remove-file"
									name="Close24"
									size="small"
									filled="error"
									fill="neutral_00_100"
									shape="round"
									onClick={() => onRemove && onRemove(file.tusID, file.id)}
								/>
							</Maybe>
						</S.FileItem>
					)}
					of={otherFiles}
				/>
				<Each
					render={(file) => (
						<S.FileItem key={file.file.name} gap={12}>
							<Loader />
							<Extensions type={getFileExtension(file.file.type || "")} />
							<Column css={{ width: "calc(100% - 56px)" }}>
								<Typography
									typoColor="neutral_80_100"
									typoSize="Body3_MD"
									css={{
										textOverflow: "ellipsis",
										overflow: "hidden",
										whiteSpace: "nowrap",
										width: "100%"
									}}
								>
									{file.file.name}
								</Typography>
								<Typography typoColor="neutral_60_100" typoSize="Body4">
									{_T("용량 {{size}}", { size: file.displaySize })}
								</Typography>
							</Column>
						</S.FileItem>
					)}
					of={localOtherImages}
				/>
			</Row.li>
		</S.FileWrapper>
	);
}

type ImageFileProps = {
	file?: File;
	localFile?: string;
	id?: string;
	only: boolean;
	isLoading?: boolean;
	tusID?: string;
	url?: string;
	onRemove?: (tusID: string, fileID?: string) => void;
};

function ImageFile({ file, localFile, id, only, isLoading, tusID, url, onRemove }: ImageFileProps) {
	const [imageSrc, setImageSrc] = useState<string | null>(null);
	const { downloadFiles } = useFileManager();

	useEffect(() => {
		if (!file) return;
		try {
			const reader = new FileReader();
			reader.onload = (e) => {
				setImageSrc((e.target?.result as string) || "");
			};

			reader.readAsDataURL(file);
		} catch (err) {
			console.warn("#ImageFile#", "::::::이미지 변환 실패", err);
		}
	}, []);

	const modal = useModal();

	const showPreview = () => {
		if (isLoading || !file || !tusID || !id || !onRemove) return;

		modal.show({
			id: ModalID.DOCUMENT_VIEWER,
			content: (
				<DocumentViewer
					createByID={Configure.userID}
					fileName={[file.name]}
					mimeType={file.type}
					imageList={url ? [url] : undefined}
					fileID={[id]}
					onClose={() => modal.hide(ModalID.DOCUMENT_VIEWER)}
					onRemove={() => {
						onRemove(tusID, id);
						modal.hide(ModalID.DOCUMENT_VIEWER);
					}}
					onDownload={() => {
						downloadFiles({ id: id }).then((response) => {
							downloadBlobData(response as Blob, file.name || "");
						});
					}}
				/>
			)
		});
	};

	if ((!imageSrc || !tusID) && !localFile) return;
	return (
		<S.ImageItem horizonAlign="center" only={only}>
			<img src={imageSrc ?? localFile} alt="Preview" onClick={showPreview} />
			{localFile ? (
				<Loader />
			) : (
				tusID && (
					<Maybe test={!isLoading} fallback={<FileLoader tusID={tusID} />}>
						<S.RemoveFile
							id="remove-file"
							name="Close24"
							size="small"
							filled="error"
							fill="neutral_00_100"
							shape="round"
							position={4}
							onClick={() => onRemove && onRemove(tusID, id)}
						/>
					</Maybe>
				)
			)}
		</S.ImageItem>
	);
}

function VideoFile({ file, id, only, isLoading, tusID, onRemove }: ImageFileProps) {
	const { downloadFiles } = useFileManager();
	const modal = useModal();
	const [thumbnail, setThumbnail] = useState("");

	if (!file) return;

	useEffect(() => {
		generateVideoThumbnail({ videoSource: file, seekTime: 1, thumbnailMaxHeight: 720 }, (thumbnailUrl) => {
			setThumbnail(thumbnailUrl);
		});
	}, [file]);

	const showPreview = () => {
		if (isLoading || !file || !tusID || !id || !onRemove) return;

		modal.show({
			id: ModalID.DOCUMENT_VIEWER,
			content: (
				<DocumentViewer
					createByID={Configure.userID}
					fileName={[file.name]}
					mimeType={file.type}
					fileID={[id]}
					onClose={() => modal.hide(ModalID.DOCUMENT_VIEWER)}
					onRemove={() => {
						onRemove(tusID, id);
						modal.hide(ModalID.DOCUMENT_VIEWER);
					}}
					onDownload={() => {
						downloadFiles({ id: id }).then((response) => {
							downloadBlobData(response as Blob, file.name || "");
						});
					}}
				/>
			)
		});
	};

	if (!thumbnail || !tusID) return;
	return (
		<S.ImageItem horizonAlign="center" only={only}>
			<img src={thumbnail} alt="Preview" onClick={showPreview} />
			{tusID && (
				<Maybe test={!isLoading} fallback={<FileLoader tusID={tusID} />}>
					<S.RemoveFile
						id="remove-file"
						name="Close24"
						size="small"
						filled="error"
						fill="neutral_00_100"
						shape="round"
						position={4}
						onClick={() => onRemove && onRemove(tusID, id)}
					/>
				</Maybe>
			)}
		</S.ImageItem>
	);
}

function FileLoader({ tusID }: { tusID: string }) {
	const theme = useTheme();

	const tusState = useTusUploadStateRenderer(tusID);

	if (!tusState) return;
	return (
		<Row
			verticalAlign="center"
			horizonAlign="center"
			css={{
				position: "absolute",
				width: "100%",
				height: "100%",
				background: theme.colors.neutral_80_50,
				top: "0",
				left: 0
			}}
		>
			<Row
				css={{
					width: "32px",
					height: "32px"
				}}
			>
				<CircularProgress
					value={tusState.percentage}
					pathColor={theme.colors.neutral_00_100}
					trailColor={theme.colors.neutral_70_100}
					strokeWidth={20}
				/>
			</Row>
		</Row>
	);
}
